import {
  AfterViewChecked,
  ChangeDetectorRef,
  Component,
  HostListener,
  OnDestroy,
  OnInit,
  inject,
} from '@angular/core';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { environment } from '@env/environment';
import { ILanguage } from '@interfaces/language';
import { TranslateService } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import { LanguageService } from '@services/language.service';
import { RouteLoaderService } from '@services/route-loader.service';
import { AppUpdateService } from '@services/app-update.service';
import { LanguagesState } from '@store/languages.state';
import { ModuleLoadState } from '@store/module-load.state';
import { Observable, Subscription, filter } from 'rxjs';
import { HeaderComponent } from './components/header/header.component';
import { FooterComponent } from './components/footer/footer.component';

@Component({
    selector: 'app-f4i',
    templateUrl: './app.component.html',
    imports: [HeaderComponent, RouterOutlet, FooterComponent]
})
export class AppComponent implements OnInit, OnDestroy, AfterViewChecked {
  private subscriptions: Subscription[] = [];
  private router = inject(Router);
  private changeDetectorRef = inject(ChangeDetectorRef);
  private translateService = inject(TranslateService);
  private languageService = inject(LanguageService);
  private routeLoaderService = inject(RouteLoaderService);
  private appUpdateService = inject(AppUpdateService);

  public moduleIsLoading$: Observable<boolean> = inject(Store).select(ModuleLoadState.getModuleLoadingState);
  public languages$: Observable<ILanguage[] | undefined> = inject(Store).select(LanguagesState.getLanguages);
  public inRegistration = true;

  public ngOnInit(): void {
    this.setRssFeedLink();

    // Initialize app update detection
    this.appUpdateService.init();
    this.inRegistration = this.router.url.startsWith('/register/');

    this.subscriptions.push(
      this.router.events.pipe(filter((event: any) => event instanceof NavigationEnd)).subscribe(()=> {
        this.inRegistration = this.router.url.startsWith('/register/');
      })
    );

    this.subscriptions.push(
      this.languages$.subscribe((languages) => {
        const availableLanguages = languages
          ? languages.map((x) => x.code)
          : [];

        const defaultLanguage = this.languageService.getDefault();
        this.translateService.addLangs(availableLanguages);
        this.translateService.setFallbackLang(defaultLanguage);

        // Only set the language if it hasn't been set yet
        if (!this.translateService.getCurrentLang()) {
          const defaultDisplayLanguage = this.languageService.getDisplayDefault(availableLanguages);
          this.translateService.use(defaultDisplayLanguage);
        }
      })
    );

    this.routeLoaderService.initialize(this.router);
  }

  private setRssFeedLink(): void {
    const apiBaseUrl = environment.apiUri?.replace(/\/+$/, '');

    if (!apiBaseUrl) {
      return;
    }

    const feedHref = `${apiBaseUrl}/api/rssfeed`;
    let link = document.querySelector('link[rel="alternate"][type="application/rss+xml"]') as HTMLLinkElement | null;

    if (!link) {
      link = document.createElement('link');
      link.setAttribute('rel', 'alternate');
      link.setAttribute('type', 'application/rss+xml');
      link.setAttribute('title', 'Funds4Impact Blog RSS');
      document.head.appendChild(link);
    }

    link.setAttribute('href', feedHref);
  }

  public ngOnDestroy(): void {
    this.subscriptions.forEach(x => x.unsubscribe());
  }

  public ngAfterViewChecked(): void {
    this.changeDetectorRef.detectChanges();
  }

  // This handler handles hrefs that contain a route
  // They are detected by their class 'router-link'.
  @HostListener('document:click', ['$event'])
  public handleClick(event: Event): void {
    if (event.target instanceof HTMLAnchorElement) {
      const element = event.target as HTMLAnchorElement;

      if (element.className === 'router-link') {
        event.preventDefault();
        const route = element?.getAttribute('href');

        if (route) {
          this.router.navigate([`/${route}`]);
        }
      }
    }
  }
}
